import React, { useState, SyntheticEvent, useEffect } from 'react'
import { translate } from '../../selectors/translations'
import { ModalWindowAction } from '../../store/ModalWindowData/types'
import ProgressBar from '../ProgressBar/ProgressBar'
import { ReactComponent as BankIdLogo } from '../../assets/img/icons/bankId-logo.svg'
import { useSelector } from "react-redux"
import { getDataForPDFReport } from "../../selectors/patientProfile"
import { ReactComponent as CheckboxIcon } from "../../assets/img/icons/check-mark.svg"
import CheckboxItem from "../CheckboxItem"
import { PdfReportSectionData, PdfReportSubsectionData } from "../../models/pdfReportData"
import { PdfItems } from "../../types/constants"
import { Link } from "react-router-dom"
import { defineIsSelectedAll, itemsWithSubsections, mapPdfSelectionToDto } from "../../utils/mapPdfSelectionToDto"
import { CreatePatientPayload } from "../../store/PatientProfile/types"
import { apiV2 } from "../../repositories/repository"
import useTranslate from "../../hooks/useTranslate"
import useSWR from 'swr'
import { formatDate } from '../../utils/datetime'
import type { GroupedBloodResultDto } from '../../sdk'

type GenericPropsFromState = {
  onConfirm: (data: ModalWindowAction<any>) => void;
  data: {
    onConfirm: () => void;
    text: string
  }
}
type PropsFromState = {
  onConfirm: (data: ModalWindowAction<any>) => void;
  data: any;
}

export const GenericConfirmationPopup = (props: GenericPropsFromState) => {
  const {data: {text, onConfirm: cb}, onConfirm} = props
  const translateLocalazy = useTranslate()

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{text}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('cancel')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--confirm"
            type="button"
            onClick={() => {
              onConfirm({action: 'cancel', data: null})
              cb()
            }}>{translateLocalazy('confirm')}</button>
        </div>
      </div>
    </div>
  )
}

export const RemoveConfirmationPopup = (props: PropsFromState) => {
  const {data, onConfirm} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Are you sure you want to archive this user?')} <br/>
            <strong>{data.firstName} {data.lastName}</strong></p>
          <p>{translate('You can restore this user later')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('cancel')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--confirm"
            type="button"
            onClick={(): void => (onConfirm({action: data.type, data: data._id}))}>{translate('Archive user')}</button>
        </div>
      </div>
    </div>
  )
}

export const DeleteConfirmationPopup = (props: PropsFromState) => {
  const {data, onConfirm} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Are you sure you want to completely delete this user?')} <br/>
            <strong>{data.firstName} {data.lastName}</strong></p>
          <p>{translate('All data related to this user will be erased. This action can be undone.')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('cancel')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--confirm"
            type="button"
            onClick={(): void => (onConfirm({action: data.type, data: data._id}))}>{translate('Delete user')}</button>
        </div>
      </div>
    </div>
  )
}

export const RestoreConfirmationPopup = (props: PropsFromState) => {
  const {data, onConfirm} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Are you sure you want to restore this user?')} <br/>
            <strong>{data.firstName} {data.lastName}</strong></p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('cancel')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--confirm"
            type="button"
            onClick={(): void => (onConfirm({action: data.type, data: data._id}))}>{translate('Restore user')}</button>
        </div>
      </div>
    </div>
  )
}

export const DownloadPdfPopup = () => {
  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Generating PDF file process starting...')}</p>
          <p><strong>{translate('We strongly recommend do not reload the page.')}</strong></p>
        </div>
        <div style={{position: 'relative'}}>
          <ProgressBar/>
        </div>
      </div>
    </div>
  )
}

export const DownloadFilePopup = (props: PropsFromState) => {
  const {onConfirm} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('File was generated. Please click button below to download it')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'downloadFile', data: null})}>{translate('Download file')}</button>
        </div>
      </div>
    </div>
  )
}

export const BankIdLoginPopup = () => {
  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <BankIdLogo style={{height: '100px', width: '100px'}}/>
          <p><strong>{translate('modal.bankId.title')}</strong></p>
          <p>{translate('modal.bankId.description')}</p>
        </div>
        <div style={{position: 'relative'}}>
          <ProgressBar/>
        </div>
      </div>
    </div>
  )
}

export const SubscriberQuestionnairePopup = (props: PropsFromState) => {
  const {data, onConfirm} = props
  const entries: { question: string; answer: string }[] = data?.entries || []
  const title = data?.name || data?.email || 'Subscribers'

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p><strong>{title}</strong></p>
          <p><strong>{data?.email}</strong></p>
          {!!data.additionalInfo && <p>Comment: <i>{data.additionalInfo || ''}</i></p>}
          {!!data.customSlot && <p>Custom slot: {data.customSlot} - {data.selectedDays.join(', ')}</p>}
          <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
              {entries.map((e, idx) => (
                <div key={idx} style={{border: '1px solid #eee', borderRadius: 6, padding: 12}}>
                  <div className="t_bold" style={{marginBottom: 6}}>{e.question}</div>
                  <div>{e.answer}</div>
                </div>
              ))}
            </div>
            {data.pointsByGroup &&
                <div style={{border: '1px solid #eee', borderRadius: 6, padding: 12, textAlign: 'left'}}>
                    <pre>
                      {JSON.stringify(data.pointsByGroup, null, 2)}
                    </pre>
                </div>}
            {entries.length === 0 && <div>{translate('noData')}</div>}
          </div>

        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--confirm"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('close')}</button>
        </div>
      </div>
    </div>
  )
}

type ConfirmationConsentProps = {
  data: CreatePatientPayload,
  onCancel: (value: boolean) => void;
  onConfirm: (data: ModalWindowAction<any>) => void;
}

const ConfirmationConsentFirst: React.FC<ConfirmationConsentProps> = (props: ConfirmationConsentProps) => {
  const {onCancel, onConfirm, data} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Have you obtained verbal consent from the patient to create an account on their behalf?')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onCancel(false)}>{translate('No')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button"
            onClick={(): void => onConfirm({action: 'createPatient', data})}>{translate('Yes')}</button>
        </div>
      </div>
    </div>
  )
}

type ConfirmationConsentSProps = {
  onConfirm: (data: ModalWindowAction<any>) => void;
  data: CreatePatientPayload,
}

const ConfirmationConsentSecond: React.FC<ConfirmationConsentSProps> = (props: ConfirmationConsentSProps) => {
  const {onConfirm, data} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Please obtain verbal consent from the patient to create an account on their behalf.')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('cancel')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button"
            onClick={(): void => onConfirm({action: 'createPatient', data})}>{translate('Consent obtained')}</button>
        </div>
      </div>
    </div>
  )
}

export const PatientConsentConfirmation = (props: PropsFromState) => {
  const [isConsent, setConsent] = useState(true)
  const {onConfirm, data} = props

  return isConsent
    ? <ConfirmationConsentFirst onCancel={setConsent} onConfirm={onConfirm} data={data}/>
    : <ConfirmationConsentSecond onConfirm={onConfirm} data={data}/>
}

export const IeWarningPopup = (props: PropsFromState) => {
  const {onConfirm} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('ieWarning')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('Ok')}</button>
        </div>
      </div>
    </div>
  )
}

export const ConfirmationConsentInstitution = (props: PropsFromState) => {
  const {onConfirm, data} = props

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('Do you have permission from patient to share patient data with other institution?')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button"
            onClick={(): void => onConfirm({action: 'addInstitution', data})}>{translate('Yes')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('No')}</button>
        </div>
      </div>
    </div>
  )
}

export const AppVersionPopup = (props: PropsFromState) => {
  const {onConfirm, data} = props
  const [appVersion, setAppVersion] = useState(data.version)
  const translateLocalazy = useTranslate()

  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translateLocalazy('versioning.modal.title')}</p>
        </div>
        <div className="card-box no-borders">
          <div className="card-input-box">
            <div style={{marginBottom: '9px'}}
                 className="input-holder flex-item"><label>{translateLocalazy('versioning.hcp')}</label>
              <input
                value={appVersion.hcpPortal}
                onChange={(e: SyntheticEvent): void => {
                  e.persist()
                  setAppVersion({...appVersion, ...{'hcpPortal': (e.target as HTMLInputElement).value}})
                }}
                type="text"
                name="hcpPortal" placeholder={translate('Type here...')}/>
            </div>
            <div className="input-holder flex-item" style={{marginBottom: '9px'}}>
              <label>{translateLocalazy('versioning.pp')}</label>
              <input
                value={appVersion.patientPortal}
                onChange={(e: SyntheticEvent): void => {
                  e.persist()
                  setAppVersion({...appVersion, ...{'patientPortal': (e.target as HTMLInputElement).value}})
                }}
                type="text"
                name="pp" placeholder={translate('Type here...')}/>
            </div>
            <div style={{marginBottom: '9px'}}
                 className="input-holder flex-item"><label>{translateLocalazy('versioning.ios')}</label>
              <input
                value={appVersion.ios}
                onChange={(e: SyntheticEvent): void => {
                  e.persist()
                  setAppVersion({...appVersion, ...{'ios': (e.target as HTMLInputElement).value}})
                }}
                type="text"
                name="iOS" placeholder={translate('Type here...')}/>
            </div>
            <div style={{marginBottom: '9px'}}
                 className="input-holder flex-item"><label>{translateLocalazy('versioning.android')}</label>
              <input
                value={appVersion.android}
                onChange={(e: SyntheticEvent): void => {
                  e.persist()
                  setAppVersion({...appVersion, ...{'android': (e.target as HTMLInputElement).value}})
                }}
                type="text"
                name="android" placeholder={translate('Type here...')}/>
            </div>
            <div className="input-holder flex-item"><label>{translateLocalazy('versioning.ss')}</label>
              <input
                value={appVersion.symptomSurvey}
                onChange={(e: SyntheticEvent): void => {
                  e.persist()
                  setAppVersion({...appVersion, ...{'symptomSurvey': (e.target as HTMLInputElement).value}})
                }}
                type="text"
                name="iOS" placeholder={translate('Type here...')}/>
            </div>
          </div>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button"
            onClick={(): void => onConfirm({action: 'setAppVersion', data: appVersion})}>{translate('save')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('cancel')}</button>
        </div>
      </div>
    </div>
  )
}

export const RefreshLoginCodePopup = (props: PropsFromState) => {
  const {onConfirm, data} = props
  return (
    <div className="confirmation_wapper">
      <div className="confirmation_box">
        <div className="confirmation_message">
          <p>{translate('modal.confirmation.refreshLoginCode')}</p>
        </div>
        <div className="confirmation_btns-box">
          <button
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button"
            onClick={(): void => onConfirm({action: 'refreshLoginCode', data})}>{translate('Yes')}</button>
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={(): void => onConfirm({action: 'cancel', data: null})}>{translate('No')}</button>
        </div>
      </div>
    </div>
  )
}

const getLatestDateIssuedFromGroup = (group?: GroupedBloodResultDto): string | undefined => {
  if (!group?.data?.length) return undefined
  return group.data
    .map((r) => r.dateIssued)
    .filter(Boolean)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
}

export const ItemsForPDFReportPopup = (props: PropsFromState) => {
  const {onConfirm, data} = props
  const initialItemsSet: any = useSelector(getDataForPDFReport)
  const [itemsSet, setItemsSet] = useState(initialItemsSet)
  const [areCheckboxesSelected, setAreCheckboxesSelected] = useState(true)
  const [encryptPdf, setEncryptPdf] = useState(false)
  const oldStyleSs = itemsSet[PdfItems.OldStyleSs]?.isSelected ?? false
  const translateLocalazy = useTranslate()

  const {data: bloodResultsGroups} = useSWR(
    data?.userId ? ['api.bloodResults.getPatientResults', data.userId] : null,
    () => apiV2.bloodResults.getPatientResults(data.userId),
    {revalidateOnFocus: false},
  )

  useEffect(() => {
    if (bloodResultsGroups?.length) {
      const bloodResultsAsSections = bloodResultsGroups.map((group: GroupedBloodResultDto) => {
        const labName = group.data?.[0]?.labName
        const date = getLatestDateIssuedFromGroup(group)
        return new PdfReportSectionData({
          id: group.serviceRequestId,
          date: date ? formatDate(date) : '',
          type: PdfItems.BloodResults,
          title: labName,
          subsections: [],
        })
      })
      setItemsSet((prev: any) => ({
        ...prev,
        [PdfItems.BloodResults]: bloodResultsAsSections,
      }))
    }
  }, [bloodResultsGroups])

  const handleSelectAll = () => {
    setItemsSet((prevState: any) => {
      if (areCheckboxesSelected) {
        setAreCheckboxesSelected(false)
        return Object.keys(prevState).reduce((prev: any, key) => {
          // @ts-ignore
          const item = prevState[key]
          if (Array.isArray(item)) {
            prev[key] = item.map(item => {
              if (itemsWithSubsections.includes(item.type)) {
                item.deselectAllSubsections()
              }
              item.deselect()
              return item
            })
          } else {
            item.deselect()
            prev[key] = item
          }
          return prev
        }, {})
      }
      setAreCheckboxesSelected(true)
      return Object.keys(prevState).reduce((prev: any, key) => {
        // @ts-ignore
        const item = prevState[key]
        if (Array.isArray(item)) {
          prev[key] = item.map(item => {
            if (itemsWithSubsections.includes(item.type)) {
              item.selectAllSubsections()
            }
            item.select()
            return item
          })
        } else {
          item.select()
          prev[key] = item
        }
        return prev
      }, {})
    })
  }

  const changeSelection = (itemMetaData: any) => {
    // setAreCheckboxesSelected(!areCheckboxesSelected);
    if (itemMetaData.type === PdfItems.SubSections) {
      setItemsSet((prevState: any) => {
        const parentSection = prevState[itemMetaData.parentType].find((item: any) => {
          return item.id === itemMetaData.parentId
        }) as PdfReportSectionData
        if (!parentSection) {
          return {
            ...prevState,
          }
        }
        const newItem = prevState[itemMetaData.parentType].map((item: any) => {
          if (item.id === itemMetaData.parentId) {
            item.toggleSubsection(itemMetaData.id)
            item.refreshSelfSelection()
          }
          return item
        })
        return {
          ...prevState,
          [itemMetaData.parentType]: newItem
        }
      })
      return
    }
    if (
      [
        PdfItems.Status,
        PdfItems.Diagnosis,
        PdfItems.Summary,
        PdfItems.Recommendation,
        PdfItems.EditHistory,
        PdfItems.ViewHistory,
        PdfItems.Downloads,
        PdfItems.OldStyleSs,
      ].includes(itemMetaData.type)
    ) {
      setItemsSet((prevState: any) => {
        // @ts-ignore
        const newItem = prevState[itemMetaData.type]
        newItem.toggleSelection()
        return {
          ...prevState,
          [itemMetaData.type]: newItem
        }
      })
      return
    }
    setItemsSet((prevState: any) => {
      // @ts-ignore
      const newItem = prevState[itemMetaData.type].map(item => {
        if (item.id === itemMetaData.id) {
          item.toggleSelection()
        }
        return item
      })
      return {
        ...prevState,
        [itemMetaData.type]: newItem
      }
    })
  }

  useEffect(() => {
    setAreCheckboxesSelected(defineIsSelectedAll(itemsSet))
  }, [itemsSet])

  const handleSuccessClick = () => {
    onConfirm({action: 'setPdfReportType', data: 'doctor'})
  }

  const handleSavePdfClick = (userId: string) => {
    const pdfGenerationSettings = mapPdfSelectionToDto(itemsSet)
    const anamnesis = pdfGenerationSettings.anamnesisesNew.filter(e => e.sectionsVisible.length > 0)
    const cognitive = pdfGenerationSettings.cognitives.filter(e => e.sectionsVisible.length > 0)
    const shared = [...anamnesis, ...cognitive]
    // enabling visibility of answer inside mobile app with direct request because we don't need state
    shared.forEach(e => apiV2.questionerAnswer.toggleVisibilityToPatient(e.id, e.sectionsVisible))
    apiV2.questionerAnswer.notifyResultsAvailable(userId)

    onConfirm({action: 'setPdfReportType', data: 'patient'})
  }

  const handleCancelClick = () => {
    onConfirm({action: 'cancel', data: null})
    // window.removeEventListener('storage', data.storageListener);
  }

  return (
    <>
      <div className="pb-20 t_center">
        <h2 className="m-0">{translate('download_pdf_report')}</h2>
      </div>

      <div className="popup-pdf-content-holder ph-10 pb-20">
        <div className="flex align-center margin-me--btm pb-5">
          <div className="custom-checkbox_wrapper margin-right">
            <input
              checked={areCheckboxesSelected}
              onChange={handleSelectAll}
              className="hidden-checkbox"
              type="checkbox"/>
            <div className="pseudo-checkbox_holder"></div>
            <CheckboxIcon className="pseudo-checkbox_icon"/>
          </div>
          <span
            style={{cursor: 'pointer'}}
            onClick={handleSelectAll}>
                  {translate('patientAccount.newPatient.patientTasks.selectAll')}
              </span>
        </div>
        <CheckboxItem data={itemsSet[PdfItems.Status]} onChangeCb={changeSelection}></CheckboxItem>
        {itemsSet[PdfItems.Diagnosis] &&
            <CheckboxItem data={itemsSet[PdfItems.Diagnosis]} onChangeCb={changeSelection}></CheckboxItem>
        }
        {itemsSet[PdfItems.Summary] &&
            <CheckboxItem data={itemsSet[PdfItems.Summary]} onChangeCb={changeSelection}></CheckboxItem>
        }
        {itemsSet[PdfItems.Recommendation] &&
            <CheckboxItem data={itemsSet[PdfItems.Recommendation]} onChangeCb={changeSelection}></CheckboxItem>
        }
        {itemsSet[PdfItems.BloodResults]?.length > 0 &&
          itemsSet[PdfItems.BloodResults].map((bloodResult: PdfReportSectionData) => (
            <CheckboxItem
              key={bloodResult.id}
              data={bloodResult}
              onChangeCb={changeSelection}
            />
          ))
        }
        {itemsSet[PdfItems.OldStyleSs] &&
            <CheckboxItem data={itemsSet[PdfItems.OldStyleSs]} onChangeCb={changeSelection}
                          description={translateLocalazy('pdf.old-ss-checkmark.description')}></CheckboxItem>
        }
        {
          itemsSet[PdfItems.AnamnesisNew].map((anamnesis: PdfReportSectionData) => {
            const subsections = anamnesis.subsections.map((subsection: PdfReportSubsectionData) => {
              return <CheckboxItem key={subsection.id} data={subsection} onChangeCb={changeSelection}></CheckboxItem>
            })
            return (
              <div key={anamnesis.id}>
                <CheckboxItem data={anamnesis} onChangeCb={changeSelection}></CheckboxItem>
                {!anamnesis.areSubsectionsClosed &&
                    <div className="pl-40">
                      {subsections}
                    </div>
                }
              </div>
            )
          })
        }
        {itemsSet[PdfItems.Cognitive].map((cognitive: PdfReportSectionData) => {
          return (
            <div key={cognitive.id}>
              <CheckboxItem key={cognitive.id} data={cognitive}
                            onChangeCb={changeSelection}></CheckboxItem>
              <div className="pl-40">
                {cognitive.subsections.map((subsection: PdfReportSubsectionData) => {
                  return <CheckboxItem key={subsection.id} data={subsection}
                                       onChangeCb={changeSelection}></CheckboxItem>
                })}
              </div>
            </div>
          )
        })
        }
        {
          itemsSet[PdfItems.SymptomSurveys].map((survey: PdfReportSectionData) => {
            const subsections = survey.subsections.map((subsection: PdfReportSubsectionData) => {
              return <CheckboxItem key={subsection.id} data={subsection} onChangeCb={changeSelection}></CheckboxItem>
            })
            return (
              <div key={survey.id}>
                <CheckboxItem data={survey} onChangeCb={changeSelection}></CheckboxItem>
                {!survey.areSubsectionsClosed &&
                    <div className="pl-40" hidden={oldStyleSs}>
                      {subsections}
                    </div>
                }
              </div>
            )
          })
        }
        {
          itemsSet[PdfItems.FAQ].map((faq: PdfReportSectionData) => {
            return (
              <div key={faq.id}>
                <CheckboxItem data={faq} onChangeCb={changeSelection}></CheckboxItem>
              </div>
            )
          })
        }
      </div>
      <div className="flex align-center ph-10 pt-10 pb-10" style={{borderTop: '1px solid #eee'}}>
        <div className="custom-checkbox_wrapper margin-right">
          <input
            checked={encryptPdf}
            onChange={() => setEncryptPdf(prev => !prev)}
            className="hidden-checkbox"
            type="checkbox"/>
          <div className="pseudo-checkbox_holder"></div>
          <CheckboxIcon className="pseudo-checkbox_icon"/>
        </div>
        <span style={{cursor: 'pointer'}} onClick={() => setEncryptPdf(prev => !prev)}>
              {translateLocalazy('pdf.encrypt-with-pnr')}
            </span>
      </div>
      <div className="confirmation_btns-box pt-20">
        <div className="pr-20">
          <Link
            to={{
              pathname: `/generate-pdf/${data.userId}`,
              search: `itemsSet=${JSON.stringify(mapPdfSelectionToDto(itemsSet))}&reportType=patient&encryptPdf=${encryptPdf}`
            }}
            target="_blank">
            <button
              onClick={() => handleSavePdfClick(data.userId)}
              className="confirmation_btn g_btn confirmation_btn--yes"
              type="button">{translate('sendToPatientModalBtn')}
            </button>
          </Link>
        </div>
        <Link
          to={{
            pathname: `/generate-pdf/${data.userId}`,
            search: `itemsSet=${JSON.stringify(mapPdfSelectionToDto(itemsSet))}&reportType=doctor&encryptPdf=${encryptPdf}`
          }}
          target="_blank">
          <button
            onClick={handleSuccessClick}
            className="confirmation_btn g_btn confirmation_btn--yes"
            type="button">{translate('downloadReportModalBtn')}</button>
        </Link>
        <div className="pl-20">
          <button
            className="confirmation_btn g_btn confirmation_btn--cancel"
            type="button"
            onClick={handleCancelClick}>{translate('cancel')}</button>
        </div>
      </div>
    </>
  )
}
